import React, { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import NestedItemSelect, {
  BlueprintOption,
  BlueprintSection,
} from "@components/NestedItemSelect/NestedItemSelect";
import "./style.css";
import useGetProject from "./api/useGetProject";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { Project } from "./types/Project";
import { TextField } from "@components/FormsV2";

const col = (id: string, header: string): ColumnDef<BlueprintOption> => ({
  id,
  header,
  cell: (props) => (props.row.original[id] as string) || "",
});

const GENERAL_OPTIONS: BlueprintOption[] = [
  { id: "executive-summary", name: "Executive Summary", selected: true },
  { id: "solution-summary", name: "Solution Summary", selected: true },
  { id: "rate-table", name: "Rate Table", selected: true },
  { id: "payment-term", name: "Payment Term", selected: true },
];

const buildSections = (project: Project | null): BlueprintSection[] => [
  {
    id: "general",
    name: "General",
    selected: true,
    options: GENERAL_OPTIONS,
    columns: [col("name", "General Sections to Include")],
  },
  {
    id: "professional-services",
    name: "Professional Services",
    selected: true,
    options: (project?.services?.filter(
      (s) => s.type == "professional_services"
    ) ?? []) as BlueprintOption[],
    columns: [
      col("name", "Professional Services To Include"),
      col("quantity", "QTY"),
      col("hours", "Unit Hrs"),
      col("resource", "Resource"),
    ],
  },
  {
    id: "managed-services",
    name: "Managed Services",
    selected: true,
    options: (project?.services?.filter((s) => s.type == "managed_services") ??
      []) as BlueprintOption[],
    columns: [
      col("name", "Managed Services To Include"),
      col("quantity", "QTY"),
      col("price", "Monthly Price"),
      col("cost", "Monthly Cost"),
    ],
  },
  {
    id: "vendor-quotes",
    name: "Vendor Quotes",
    selected: true,
    options: (project?.quotes ?? []) as BlueprintOption[],
    columns: [
      col("name", "Vendor Quotes To Include"),
      col("cost", "One Time Cost"),
      col("recCost", "Recurring Cost"),
      col("revenue", "One Time Revenue"),
      col("recRevenue", "Recurring Revenue"),
    ],
  },
  {
    id: "product",
    name: "Product",
    selected: true,
    options: (project?.products ?? []) as BlueprintOption[],
    columns: [
      col("name", "Products To Include"),
      col("mfrPart", "MFR Part"),
      col("quantity", "Quantity"),
      col("units", "Units"),
      col("price", "Unit Price"),
    ],
  },
  {
    id: "governance",
    name: "Governance",
    selected: true,
    options: (project?.governances ?? []) as BlueprintOption[],
    columns: [
      col("name", "Governances To Include"),
      col("phase", "Phase"),
      col("resource", "Resource"),
      col("calculation", "Calc Hrs By"),
      col("hours", "Hours"),
    ],
  },
  {
    id: "resources",
    name: "Resources",
    selected: true,
    options: (project?.resources ?? []) as BlueprintOption[],
    columns: [
      col("name", "Resources To Include"),
      col("lob", "LOB/Category"),
      col("hourlyRate", "Hourly Rate"),
      col("expenseRate", "Travel/Expense Rate"),
    ],
  },
];

interface Props {
  projectId: string;
  show: boolean;
  onClose: () => void;
}

const CreateBlueprintModal: React.FC<Props> = ({
  projectId,
  show,
  onClose,
}) => {
  const { project, isLoading } = useGetProject(projectId);
  const [blueprint, setBlueprint] = useState<{ name: string; code: string }>({
    name: "",
    code: "",
  });
  const [sections, setSections] = useState<BlueprintSection[]>(() =>
    buildSections(project || null)
  );

  useEffect(() => {
    if (show && !isLoading && project) setSections(buildSections(project));
  }, [show, isLoading]);

  return (
    <ScopeStackModal
      className="createBlueprintFromProjectModal"
      show={show}
      modalTitle="Create Blueprint from Project"
      modalBody={
        isLoading ? (
          <ScopeStackSpinner />
        ) : (
          <>
            <div className="context">Name Blueprint</div>
            <div className="description">
              Enter the blueprint name and any additional identifying details.
            </div>
            <div className="fields">
              <TextField
                id="name"
                label="Blueprint Name"
                placeholder="Enter Blueprint Name"
                onChange={(e) =>
                  setBlueprint({ ...blueprint, name: e.target.value })
                }
              />
              <TextField
                id="code"
                label="Code"
                placeholder="Enter Code (optional)"
                onChange={(e) =>
                  setBlueprint({ ...blueprint, code: e.target.value })
                }
              />
            </div>
            <div className="context">Customize Blueprint</div>
            <div className="description">
              Select the items you wish to include on this blueprint.
            </div>
            <NestedItemSelect
              sections={sections}
              onSectionsChange={setSections}
            />
          </>
        )
      }
      button1Text="Cancel"
      button2Text="Create Blueprint"
      handleClose={onClose}
      handleButton1Click={onClose}
      handleButton2Click={() => {
        // TODO: implement blueprint creation
      }}
    />
  );
};

export default CreateBlueprintModal;
